import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import { JMCLink } from "@components/JMCLink/JMCLink";
import { MedicalExpertSchema } from "@components/MedicalExpertSchema/MedicalExpertSchema";
import { SectionImpression } from "@components/SectionImpression/SectionImpression";
import { Avatar } from "@jmc/solid-design-system/src/components/atoms/Avatar/Avatar";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { CMSPersonProps } from "@types";
import { formatPersonName, getInitials } from "@utils/person-helper";
import classnames from "classnames";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";

import style from "./style.module.scss";

export interface MedicalExpertCardProps {
    medicalExpert: CMSPersonProps;
    medicalExpertText: string;
    pageTitle: string;
}

export const MedicalExpertCard = ({
    medicalExpert,
    medicalExpertText,
    pageTitle,
}: MedicalExpertCardProps): JSX.Element => {
    const { t } = useTranslation();
    const relatedTos = medicalExpert.related_to
        .map(({ specialtyTitle }) => t(specialtyTitle))
        .filter((v) => v?.length > 0)
        .join(", ");
    let medexpText = medicalExpertText?.replace("{{MEDICAL_EXPERT_NAME}}", formatPersonName(medicalExpert));
    medexpText = medexpText?.replace("{{LEGAL_ENTITY_NAME}}", t("Janssen Pharmaceutica NV", { ns: "common" }));
    const medicalExpertName = formatPersonName(medicalExpert);
    const avatarInitials = getInitials(medicalExpert);
    const placement = `${pageTitle}:Reviewed by`;

    return (
        <div className={classnames(style.medicalExpertCard)} data-test-id="MedicalExpertCard">
            <SectionImpression name={"Reviewed by"} position={1}>
                <Fragment>
                    <Typography variant="h4" color="secondary" weight="500">
                        {t("Reviewed by", { ns: "common" })}
                    </Typography>
                    <div className={style.content} data-test-id="MedicalExpertCard.Content">
                        <div className={style.avatarAndName} data-test-id="AvatarNameContainer">
                            {/* see comment on https://jira.jnj.com/browse/AFWN-8751: different size for mobile v. desktop! */}
                            <div className={style.avatarMobile}>
                                <Avatar
                                    size="medium"
                                    imageUrl={medicalExpert?.avatar_image?.url}
                                    avatarInitials={avatarInitials}
                                    dataTestId="MedicalExpertCard.Avatar.Mobile"
                                />
                            </div>
                            <div className={style.avatarDesktop}>
                                <Avatar
                                    size="xl"
                                    imageUrl={medicalExpert?.avatar_image?.url}
                                    avatarInitials={avatarInitials}
                                    dataTestId="MedicalExpertCard.Avatar.Desktop"
                                />
                            </div>
                            <div>
                                <div className={style.name} data-test-id="MedicalExpertCard.NameContainer">
                                    <Typography variant="h5" data-test-id="MedicalExpertCard.Name">
                                        {medicalExpertName}
                                    </Typography>
                                </div>
                                {relatedTos?.length > 0 && (
                                    <div
                                        className={style.specialty}
                                        data-test-id="MedicalExpertCard.RelatedTosContainer"
                                    >
                                        <Typography
                                            variant="body"
                                            color="dark"
                                            data-test-id="MedicalExpertCard.Name.RelatedTos"
                                        >
                                            {relatedTos}
                                        </Typography>
                                    </div>
                                )}
                                <div className={style.job_linkedIn_container_mobile}>
                                    <div className={style.jobtitle} data-test-id="MedicalExpertCard.JobTitleContainer">
                                        <Typography
                                            variant="body"
                                            color="light"
                                            size="default"
                                            data-test-id="MedicalExpertCard.JobTitle"
                                        >
                                            {medicalExpert?.job_title}
                                        </Typography>
                                    </div>
                                    {medicalExpert?.linkedin_profile?.length > 0 && (
                                        <div
                                            className={style.linkedin}
                                            data-test-id="MedicalExpertCard.LinkedinContainer"
                                        >
                                            <JMCLink
                                                external
                                                url={`https://www.linkedin.com/in/${medicalExpert.linkedin_profile}`}
                                                data-test-id={`MedicalExpertCard.Linkedin.link`}
                                                name="View Linkedin profile"
                                                placement={placement}
                                            >
                                                <Typography
                                                    link
                                                    component="span"
                                                    data-test-id={`MedicalExpertCard.Linkedin.label`}
                                                >
                                                    {t("View Linkedin profile", { ns: "common" })}
                                                </Typography>
                                            </JMCLink>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={style.job_linkedIn_container_desktop}>
                            <div data-test-id="MedicalExpertCard.JobTitleContainer">
                                <Typography
                                    variant="body"
                                    color="light"
                                    size="default"
                                    data-test-id="MedicalExpertCard.JobTitle"
                                >
                                    {medicalExpert.job_title}
                                </Typography>
                            </div>
                            {medicalExpert?.linkedin_profile?.length > 0 && (
                                <div data-test-id="MedicalExpertCard.LinkedinContainer">
                                    <JMCLink
                                        external
                                        url={`https://www.linkedin.com/in/${medicalExpert.linkedin_profile}`}
                                        data-test-id={`MedicalExpertCard.Linkedin.link`}
                                        name="View Linkedin profile"
                                        placement={placement}
                                    >
                                        <Typography
                                            link
                                            component="span"
                                            data-test-id={`MedicalExpertCard.Linkedin.label`}
                                        >
                                            {t("View Linkedin profile", { ns: "common" })}
                                        </Typography>
                                    </JMCLink>
                                </div>
                            )}
                        </div>
                        <div className={style.DescriptionContainer}>
                            <div className={style.reviewed} data-test-id="MedicalExpertCard.MedExpTextContainer">
                                <Typography variant="body" color="dark" data-test-id="MedicalExpertCard.MedExpText">
                                    <JMCHtml>{medexpText}</JMCHtml>
                                </Typography>
                            </div>
                        </div>
                    </div>
                    <MedicalExpertSchema medicalExpert={medicalExpert} medicalExpertName={medicalExpertName} />
                </Fragment>
            </SectionImpression>
        </div>
    );
};

export default MedicalExpertCard;
